import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import Card from "../components/custom/Card";
import { apiUrl } from "../config";

interface ReportItem {
    accountName: string;
    paymentMode?: string;
    amount: number;
}

interface ReportGroup {
    groupName: string;
    items: ReportItem[];
    total: number;
}

interface ReceiptItem {
    accountName: string;
    openingBalance: number;
    totalDebit: number;
    totalCredit: number;
    amount: number;
}

interface ReceiptGroup {
    groupName: string;
    items: ReceiptItem[];
    total: number;
}

interface ReportType {
    income: ReportGroup[];
    expenditure: ReportGroup[];
    receipts: ReceiptGroup[];
    totalIncome: number;
    totalExpenditure: number;
    totalReceipt: number;
    excessOrDeficit: number;
    isExcess: boolean;
}

const generateFinancialYears = () => {
    const years: string[] = [];
    const currentYear = new Date().getFullYear();
    for (let y = 2020; y <= currentYear + 1; y++) {
        years.push(`${y}-${y + 1}`);
    }
    return years.reverse();
};

const IncomeExpenseReport = () => {

    const [financialYear, setFinancialYear] = useState("");
    const [report, setReport] = useState<ReportType>({
        income: [],
        expenditure: [],
        receipts: [],
        totalIncome: 0,
        totalExpenditure: 0,
        totalReceipt: 0,
        excessOrDeficit: 0,
        isExcess: true
    });

    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    const [includeAnnexure, setIncludeAnnexure] = useState(false);

    const fetchReport = async (fy: string) => {
        if (!fy) return;
        try {
            const params = new URLSearchParams();
            params.append("financialYear", fy);
            const response = await axios.get(
                `${apiUrl}/transaction/reports/?${params.toString()}`
            );
            if (response.data) {
                setReport(response.data);
                setExpandedGroups(new Set());
                setIncludeAnnexure(false);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        fetchReport(financialYear);
    }, [financialYear]);

    const toggleGroup = (groupName: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(groupName) ? next.delete(groupName) : next.add(groupName);
            return next;
        });
    };

    const toggleAnnexure = () => {
        setIncludeAnnexure(prev => !prev);
    };

    const getDisplayGroupName = (groupName: string) => {
    if (groupName.toLowerCase() === "employee") return "Employee Salary";
    return groupName;
};

const handlePdfDownload = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const commonStyles = {
        fontSize: 9,
        cellPadding: 2,
        textColor: [0, 0, 0] as [number, number, number],
    };

    const commonHeadStyles = {
        textColor:  [0, 0, 0]       as [number, number, number],
        fillColor:  [255, 255, 255] as [number, number, number],
        fontStyle:  "bold"          as const,
        fontSize:   10,
        halign:     "center"        as const,
        lineColor:  [0, 0, 0]       as [number, number, number],
        lineWidth:  0.3,
    };

    // ── Header ──
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Income & Expenditure Report", pageWidth / 2, 15, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Financial Year: ${financialYear}`, pageWidth / 2, 22, { align: "center" });

    // ── Expenditure Body ──
    const expenditureBody: any[] = [];
    report.expenditure.forEach((group) => {
        expenditureBody.push([
            { content: getDisplayGroupName(group.groupName), styles: { fontStyle: "bold" } },
            { content: group.total.toFixed(2), styles: { halign: "right", fontStyle: "bold" } }
        ]);
    });
    expenditureBody.push([
        {
            content: report.isExcess
                ? "Excess of Income Over Expenditure"
                : "Deficit (Expenditure Over Income)",
            styles: { fontStyle: "italic" }
        },
        {
            content: Math.abs(report.excessOrDeficit).toFixed(2),
            styles: { halign: "right", fontStyle: "italic" }
        }
    ]);
    expenditureBody.push([
        {
            content: "Total",
            styles: {
                fontStyle: "bold",
                lineColor: [0, 0, 0] as [number, number, number],
                lineWidth: { top: 0.3, bottom: 0, left: 0, right: 0 } as any
            }
        },
        {
            content: report.totalIncome.toFixed(2),
            styles: {
                halign: "right" as const,
                fontStyle: "bold",
                lineColor: [0, 0, 0] as [number, number, number],
                lineWidth: { top: 0.3, bottom: 0, left: 0, right: 0 } as any
            }
        }
    ]);

    // ── Income Body ──
const incomeBody: any[] = [];
report.income.forEach((group) => {
    group.items.forEach((item) => {
        incomeBody.push([
            { content: `  ${item.accountName} (${item.paymentMode || ""})` },
            { content: item.amount.toFixed(2), styles: { halign: "right" } }
        ]);
    });
});

    incomeBody.push([
        {
            content: "Total",
            styles: {
                fontStyle: "bold",
                lineColor: [0, 0, 0] as [number, number, number],
                lineWidth: { top: 0.3, bottom: 0, left: 0, right: 0 } as any
            }
        },
        {
            content: report.totalIncome.toFixed(2),
            styles: {
                halign: "right" as const,
                fontStyle: "bold",
                lineColor: [0, 0, 0] as [number, number, number],
                lineWidth: { top: 0.3, bottom: 0, left: 0, right: 0 } as any
            }
        }
    ]);

    const expRowsBeforeTotal = expenditureBody.length - 1;
    const incRowsBeforeTotal = incomeBody.length - 1;
    const rowDiff = Math.abs(expRowsBeforeTotal - incRowsBeforeTotal);

    if (rowDiff > 0) {
        const spacerRow = [{ content: "" }, { content: "" }];
        if (expRowsBeforeTotal < incRowsBeforeTotal) {
            const lastRow = expenditureBody.pop();
            for (let i = 0; i < rowDiff; i++) expenditureBody.push([...spacerRow]);
            expenditureBody.push(lastRow!);
        } else {
            const lastRow = incomeBody.pop();
            for (let i = 0; i < rowDiff; i++) incomeBody.push([...spacerRow]);
            incomeBody.push(lastRow!);
        }
    }

    const startY = 30;
    const tableHeight = Math.max(expenditureBody.length, incomeBody.length) * 7 + 10;

    autoTable(doc, {
        startY,
        margin: { left: 10 },
        head: [["Expenditure", "Amount"]],
        body: expenditureBody,
        theme: "plain",
        tableWidth: 90,
        styles: commonStyles,
        headStyles: commonHeadStyles,
        columnStyles: {
            0: { halign: "left", cellWidth: 65 },
            1: { halign: "right", cellWidth: 25 },
        },
        didDrawPage: (data) => {
            const endY = (data.cursor?.y ?? startY + tableHeight);
            doc.setLineWidth(0.3);
            doc.setDrawColor(0, 0, 0);
            // Outer box
            doc.rect(10, startY, 90, endY - startY);
            doc.line(75, startY, 75, endY);
        }
    });

    autoTable(doc, {
        startY,
        margin: { left: 102 },
        head: [["Income", "Amount"]],
        body: incomeBody,
        theme: "plain",
        tableWidth: 90,
        styles: commonStyles,
        headStyles: commonHeadStyles,
        columnStyles: {
            0: { halign: "left", cellWidth: 65 },
            1: { halign: "right", cellWidth: 25 },
        },
        didDrawPage: (data) => {
            const endY = (data.cursor?.y ?? startY + tableHeight);
            doc.setLineWidth(0.3);
            doc.setDrawColor(0, 0, 0);
            doc.rect(102, startY, 90, endY - startY);
            doc.line(167, startY, 167, endY);
        }
    });


if (includeAnnexure) {
    doc.addPage();
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Annexure — Income & Expenditure Details", pageWidth / 2, 15, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Financial Year: ${financialYear}`, pageWidth / 2, 22, { align: "center" });

    const annexureStartY = 28;

    const incomeAnnexureBody: any[] = [];
    report.income.forEach((group) => {
        group.items.forEach((item) => {
            incomeAnnexureBody.push([
                { content: `    ${(item.accountName)}` },
                { content: item.amount.toFixed(2), styles: { halign: "right" as const } }
            ]);
        });
    });
    incomeAnnexureBody.push([
        {
            content: "Grand Total",
            styles: { fontStyle: "bold" as const }
        },
        {
            content: report.totalIncome.toFixed(2),
            styles: { halign: "right" as const, fontStyle: "bold" as const }
        }
    ]);

    autoTable(doc, {
        startY: annexureStartY,
        margin: { left: 10, right: 10 },
        head: [["Account Name",  "Amount"]],
        body: incomeAnnexureBody,
        theme: "grid",
        styles: {
            fontSize: 9,
            cellPadding: 2,
            textColor: [0, 0, 0] as [number, number, number],
            lineColor: [0, 0, 0] as [number, number, number],
            lineWidth: 0.2,
        },
        headStyles: {
            textColor: [0, 0, 0] as [number, number, number],
            fillColor: [255, 255, 255] as [number, number, number],
            fontStyle: "bold" as const,
            fontSize: 10,
            halign: "center" as const,
            lineColor: [0, 0, 0] as [number, number, number],
            lineWidth: 0.3,
        },
        columnStyles: {
            0: { halign: "left", cellWidth: 130 },
            1: { halign: "center", cellWidth: 40 },
            2: { halign: "right", cellWidth: 20 },
        },
    });

    // ========== EXPENDITURE ==========
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Expenditure Details", 10, (doc as any).lastAutoTable.finalY + 10);

  
    const expenditureAnnexureBody: any[] = [];
report.expenditure.forEach((group) => {
   
    expenditureAnnexureBody.push([
        {
            content: getDisplayGroupName(group.groupName),
            styles: {
                fontStyle: "bold" as const,
                fillColor: [240, 240, 240] as [number, number, number],
                halign: "left" as const
            }
        },
        {
            content: group.total.toFixed(2),
            styles: {
                fontStyle: "bold" as const,
                fillColor: [240, 240, 240] as [number, number, number],
                halign: "right" as const  
            }
        }
    ]);
    group.items.forEach((item) => {
        expenditureAnnexureBody.push([
            { content: `    ${(item.accountName)}` },
            { content: item.amount.toFixed(2), styles: { halign: "right" as const } }
        ]);
    });
    
});
expenditureAnnexureBody.push([
    {
        content: "Grand Total",
        styles: { fontStyle: "bold" as const }
    },
    {
        content: report.totalExpenditure.toFixed(2),
        styles: { halign: "right" as const, fontStyle: "bold" as const }
    }
]);

    autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 15,
        margin: { left: 10, right: 10 },
        head: [["Account Name", "Amount"]],
        body: expenditureAnnexureBody,
        theme: "grid",
        styles: {
            fontSize: 9,
            cellPadding: 2,
            textColor: [0, 0, 0] as [number, number, number],
            lineColor: [0, 0, 0] as [number, number, number],
            lineWidth: 0.2,
        },
        headStyles: {
            textColor: [0, 0, 0] as [number, number, number],
            fillColor: [255, 255, 255] as [number, number, number],
            fontStyle: "bold" as const,
            fontSize: 10,
            halign: "center" as const,
            lineColor: [0, 0, 0] as [number, number, number],
            lineWidth: 0.3,
        },
        columnStyles: {
            0: { halign: "left", cellWidth: 160 },
            1: { halign: "right", cellWidth: 30 },
        },
    });

    // ========== RECEIPT ==========
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Cash / Bank Balance Details", 10, (doc as any).lastAutoTable.finalY + 10);

    const receiptAnnexureBody: any[] = [];
    report.receipts.forEach((group) => {
        receiptAnnexureBody.push([
            {
                content: group.groupName,
                colSpan: 2,
                styles: {
                    fontStyle: "bold" as const,
                    fillColor: [240, 240, 240] as [number, number, number],
                    halign: "left" as const
                }
            }
        ]);
        group.items.forEach((item) => {
            receiptAnnexureBody.push([
                { content: `    ${item.accountName}` },
                {
                    content: item.amount.toFixed(2),
                    styles: { halign: "right" as const, fontStyle: "bold" as const }
                }
            ]);
        });
    });
    receiptAnnexureBody.push([
        {
            content: "Grand Total",
            styles: { fontStyle: "bold" as const }
        },
        {
            content: report.totalReceipt.toFixed(2),
            styles: { halign: "right" as const, fontStyle: "bold" as const }
        }
    ]);

    autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 15,
        margin: { left: 10, right: 10 },
        head: [["Account Name", "Balance"]],
        body: receiptAnnexureBody,
        theme: "grid",
        styles: {
            fontSize: 9,
            cellPadding: 2,
            textColor: [0, 0, 0] as [number, number, number],
            lineColor: [0, 0, 0] as [number, number, number],
            lineWidth: 0.2,
        },
        headStyles: {
            textColor: [0, 0, 0] as [number, number, number],
            fillColor: [255, 255, 255] as [number, number, number],
            fontStyle: "bold" as const,
            fontSize: 9,
            halign: "center" as const,
            lineColor: [0, 0, 0] as [number, number, number],
            lineWidth: 0.3,
        },
        columnStyles: {
            0: { halign: "left", cellWidth: 160 },
            1: { halign: "right", cellWidth: 30 },
        },
    });
}

doc.save(`IE_Report_${financialYear}.pdf`);

};
    const hasData = financialYear !== "";

    return (
        <Main>
            <PageContainer title="Income and Expenditure">
                <Card title="">
                    <div className="container mt-4">

                        <Row className="mb-4 align-items-end">

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>
                                        Financial Year <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={financialYear}
                                        onChange={(e) => setFinancialYear(e.target.value)}
                                    >
                                        <option value="">-- Select Year --</option>
                                        {generateFinancialYears().map((fy) => (
                                            <option key={fy} value={fy}>{fy}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {hasData && (
                                <>
                                    <Col md={3}>
                                        <Button
                                            variant={includeAnnexure ? "primary" : "outline-secondary"}
                                            size="sm"
                                            onClick={toggleAnnexure}
                                        >
                                            {includeAnnexure
                                                ? " Annexure Included in PDF"
                                                : " Include Annexure in PDF"}
                                        </Button>
                                    </Col>

                                    <Col md={2}>
                                        <Button
                                            variant="success"
                                            onClick={handlePdfDownload}
                                            disabled={report.totalIncome === 0}
                                        >
                                            Download PDF
                                        </Button>
                                    </Col>
                                </>
                            )}
                        </Row>

                        {hasData && (
                            <Row>
                                <Col md={6}>
                                    <Table bordered size="sm">
                                        <thead style={{ backgroundColor: "#10296C", color: "#fff" }}>
                                            <tr>
                                                <th>Expenditure</th>
                                                <th className="text-end">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {report.expenditure.length > 0 ? (
                                                report.expenditure.map((group, gi) => {
                                                    const isOpen = expandedGroups.has(group.groupName);
                                                    return (
                                                        <React.Fragment key={gi}>

                                                            <tr
                                                                style={{
                                                                    backgroundColor: "#dce3f0",
                                                                    cursor: "pointer",
                                                                    userSelect: "none"
                                                                }}
                                                                onClick={() => toggleGroup(group.groupName)}
                                                            >
                                                                <td>
                                                                    <strong>
                                                                        {getDisplayGroupName(group.groupName)}
                                                                    </strong>
                                                                </td>
                                                                <td className="text-end">
                                                                    <strong>{group.total.toFixed(2)}</strong>
                                                                </td>
                                                            </tr>

                                                            {isOpen && group.items.map((item, ii) => (
                                                                <tr key={ii} style={{ backgroundColor: "#f7f9fc" }}>
                                                                    <td style={{ paddingLeft: "30px", fontSize: "13px" }}>
                                                                        {item.accountName}
                                                                    </td>
                                                                    <td className="text-end" style={{ fontSize: "13px" }}>
                                                                        {item.amount.toFixed(2)}
                                                                    </td>
                                                                </tr>
                                                            ))}

                                                        </React.Fragment>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={2} className="text-center text-muted">
                                                        No Expenditure Found
                                                    </td>
                                                </tr>
                                            )}

                                            <tr style={{ backgroundColor: "#e8f5e9" }}>
                                                <td>
                                                    <strong>
                                                        {report.isExcess
                                                            ? "Excess of Income Over Expenditure"
                                                            : "Deficit (Expenditure Over Income)"}
                                                    </strong>
                                                </td>
                                                <td className="text-end">
                                                    <strong style={{ color: report.isExcess ? "green" : "red" }}>
                                                        {Math.abs(report.excessOrDeficit).toFixed(2)}
                                                    </strong>
                                                </td>
                                            </tr>

                                            <tr style={{ backgroundColor: "#10296C", color: "#fff" }}>
                                                <th>Total</th>
                                                <th className="text-end">{report.totalIncome.toFixed(2)}</th>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                                <Col md={6}>
                                    <Table bordered size="sm">
                                        <thead style={{ backgroundColor: "#155724", color: "#fff" }}>
                                            <tr>
                                                <th>Income</th>
                                                <th className="text-end">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {report.income.length > 0 ? (
                                                report.income.map((group, gi) => (
                                                    <React.Fragment key={gi}>
                                                        {group.items.map((item, ii) => (
                                                            <tr key={ii}>
                                                                <td style={{ paddingLeft: "25px" }}>
                                                                    {item.accountName}
                                                                    {item.paymentMode && (
                                                                        <span
                                                                            className="ms-2 badge"
                                                                            style={{
                                                                                backgroundColor:
                                                                                    item.paymentMode === "cash"
                                                                                        ? "#ffc107" : "#0d6efd",
                                                                                color: "#fff",
                                                                                fontSize: "10px"
                                                                            }}
                                                                        >
                                                                            {item.paymentMode}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="text-end">
                                                                    {item.amount.toFixed(2)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={2} className="text-center text-muted">
                                                        No Income Found
                                                    </td>
                                                </tr>
                                            )}
                                            <tr style={{ backgroundColor: "#155724", color: "#fff" }}>
                                                <th>Total</th>
                                                <th className="text-end">{report.totalIncome.toFixed(2)}</th>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>

                            </Row>
                        )}
                    </div>
                </Card>
            </PageContainer>
        </Main>
    );
};

export default IncomeExpenseReport;